import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  HStack,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIsRevelioAdmin } from '@revelio/auth';
import { LoadingBox } from '@revelio/core';
import { Button, Text } from '@revelio/ui';

import type { DataBuilderModelVersions } from '../api/data-builder-api.types';
import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import { useDataBuilderRowLimit } from '../api/useDataBuilderRowLimit';
import { DataBuilderDataLagControl } from '../components/DataBuilderDataLagControl';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import type {
  DataBuilderSettingValue,
  DatasetInstanceConfig,
} from '../data-builder.store';
import {
  dataBuilderStore,
  selectActiveDatasetKey,
  selectActiveInstanceConfig,
  selectActiveInstanceId,
  selectConfigByInstance,
  selectDatasetInstances,
  selectModelVersions,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import { getDeliverableColumnConfig } from '../datasets/deliverable-column-config';
import {
  applyInheritedUoa,
  pickLastUsedUoaForTarget,
} from '../unit-of-analysis-defaulting';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import { Step2DatasetSwitcher } from './Step2DatasetSwitcher';
import {
  buildConditionContext,
  filterUnitOptionsByModels,
  getVisibleColumnKeysByBreakdown,
} from './step2-column/filter-config-by-models';
import { ModelVersionsPopover } from './step2-column/ModelVersionsPopover';
import {
  getDefaultModelVersions,
  getDependentPatchVersionKey,
  getDependentTaxonomyIdKey,
  getLatestVisiblePatchVersionValue,
  getLatestVisibleTaxonomyIdValue,
  getVisibleDefaults,
  splitModelsByCategory,
} from './step2-column/model-utils';
import { Step2ColumnsContent } from './step2-column/Step2ColumnsContent';
import { TaxonomyPopover } from './step2-column/TaxonomyPopover';
import { usePruneEventToast } from './step2-column/useTaxonomyFilterPruning';
import {
  buildStep2ValidationResult,
  getFirstValidationScrollTarget,
  step2MessagesToSectionErrorText,
} from './step2-columns-validation';

function escapeSelectorAttrValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export const DataBuilderColumnsStep = () => {
  const toast = useToast();
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const { data, isLoading } = useDataBuilderQuery();
  const activeInstanceId = useDataBuilderSelector(selectActiveInstanceId);
  const activeDatasetKey = useDataBuilderSelector(selectActiveDatasetKey);
  const activeConfig = useDataBuilderSelector(selectActiveInstanceConfig);
  const datasetInstances = useDataBuilderSelector(selectDatasetInstances);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const { goToNextStep } = useDataBuilderNavigation();
  const {
    resultsByInstanceId: rowLimitResultsByInstanceId,
    hasOverLimit: isRowLimitOverLimit,
    pendingInstanceIds,
    apiErrorInstanceIds,
    isPending: isValidatingRowLimit,
    isError: isRowLimitError,
    errorMessage: rowLimitErrorMessage,
    isReady: isRowLimitReady,
    isWaitingForCompanies,
  } = useDataBuilderRowLimit();
  const {
    setSetting,
    setUnitOfAnalysis,
    setUnitOfAnalysisSelections,
    setCompanySelections,
    setCompanySets,
    setCompanyMapper,
    applyCompanyMapper,
    setColumnKeysForBreakdown,
    setColumnKey,
    setModelVersion,
    resetModelVersions,
    setActiveInstanceId,
  } = useDataBuilderActions();
  const models = data?.models;
  const availableModels = models ?? [];
  const { taxonomyIdModels, modelVersionModels } =
    splitModelsByCategory(availableModels);
  const taxonomyDefaults = getVisibleDefaults(
    taxonomyIdModels,
    modelVersions,
    activeConfig?.settings
  );
  const modelVersionDefaults = getDefaultModelVersions(modelVersionModels);

  const config = getDeliverableColumnConfig(
    data?.deliverablesConfig ?? {},
    activeDatasetKey
  );

  const step2ValidationIssues = useMemo(() => {
    const labelMap = buildDatasetLabelMap(data?.datasets);
    const instanceLabelMap = buildDatasetInstanceLabelMap(
      datasetInstances,
      labelMap
    );
    return buildStep2ValidationResult(
      datasetInstances,
      configByInstance,
      data?.deliverablesConfig ?? {},
      modelVersions,
      instanceLabelMap
    );
  }, [
    configByInstance,
    data?.datasets,
    data?.deliverablesConfig,
    datasetInstances,
    modelVersions,
  ]);

  const instanceIdsWithErrors = useMemo(
    () => new Set(step2ValidationIssues.map((issue) => issue.instanceId)),
    [step2ValidationIssues]
  );

  const activeInstanceValidationIssue = useMemo(() => {
    if (activeInstanceId === null) {
      return undefined;
    }
    return step2ValidationIssues.find(
      (issue) => issue.instanceId === activeInstanceId
    );
  }, [activeInstanceId, step2ValidationIssues]);

  const sectionErrors = useMemo(() => {
    if (activeInstanceValidationIssue === undefined) {
      return {};
    }
    return step2MessagesToSectionErrorText(
      activeInstanceValidationIssue.messages
    );
  }, [activeInstanceValidationIssue]);

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const pendingScrollRef = useRef<{
    instanceId: string;
    section: string;
  } | null>(null);

  useEffect(() => {
    if (step2ValidationIssues.length === 0) {
      setShowValidationErrors(false);
    }
  }, [step2ValidationIssues.length]);

  useEffect(() => {
    const pending = pendingScrollRef.current;
    if (!showValidationErrors || pending === null) {
      return;
    }
    if (activeInstanceId !== pending.instanceId) {
      return;
    }
    const section = pending.section;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const escaped = escapeSelectorAttrValue(section);
        const el = document.querySelector(
          `[data-validation-error][data-section="${escaped}"]`
        );
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }, [showValidationErrors, activeInstanceId]);

  const rowLimitContinueDisabledReason = (() => {
    if (isWaitingForCompanies) {
      return 'Loading companies...';
    }
    if (isValidatingRowLimit) {
      return 'Validating row limit...';
    }
    if (isRowLimitError || isRowLimitOverLimit) {
      return 'Resolve the row limit issue to continue.';
    }
    if (!isRowLimitReady) {
      return 'Validating row limit...';
    }
    return '';
  })();
  const isRowLimitContinueDisabled = rowLimitContinueDisabledReason !== '';

  const handleContinueStep2 = useCallback(() => {
    if (isRowLimitContinueDisabled) {
      return;
    }
    if (step2ValidationIssues.length === 0) {
      goToNextStep();
      return;
    }
    const target = getFirstValidationScrollTarget(
      datasetInstances,
      step2ValidationIssues
    );
    if (target !== null) {
      pendingScrollRef.current = {
        instanceId: target.instanceId,
        section: target.section,
      };
      setActiveInstanceId(target.instanceId);
    } else {
      pendingScrollRef.current = null;
    }
    setShowValidationErrors(true);
  }, [
    datasetInstances,
    goToNextStep,
    isRowLimitContinueDisabled,
    setActiveInstanceId,
    step2ValidationIssues,
  ]);

  usePruneEventToast(toast);

  useEffect(() => {
    if (models === undefined || Object.keys(modelVersions).length > 0) {
      return;
    }

    const defaultModelVersions = getDefaultModelVersions(models);

    if (Object.keys(defaultModelVersions).length === 0) {
      return;
    }

    resetModelVersions(defaultModelVersions);
  }, [modelVersions, models, resetModelVersions]);

  // Initial breakdown / column defaults are populated at instance-creation
  // time — by `seedInstanceDefaults` for Step 1's `toggleDatasetType` and
  // Step 2 dataset-switcher's `addDatasetInstance`, and directly from the
  // saved payload by `hydrateStoreFromPayload`. This component only reacts
  // to *changes* in visible columns/breakdowns (model version swaps,
  // unit-of-analysis changes); it no longer materializes defaults itself.

  const handleSetSetting = useCallback(
    (key: string, value: DataBuilderSettingValue) => {
      if (activeInstanceId === null) {
        return;
      }
      // Reconcile columns in the same update: a setting change can hide columns
      // (e.g. `expected_hires` is unified-only), and the column-sync effect only
      // catches up on the next commit — by which point the new setting value has
      // already gone out with the now-invalid column in the `/estimate` payload.
      const visibleColumnKeysByBreakdown =
        config === undefined || activeConfig === undefined
          ? undefined
          : getVisibleColumnKeysByBreakdown(
              config,
              buildConditionContext(
                modelVersions,
                activeConfig.unitOfAnalysis,
                {
                  ...activeConfig.settings,
                  [key]: value,
                }
              )
            );
      setSetting(activeInstanceId, key, value, visibleColumnKeysByBreakdown);
    },
    [setSetting, activeInstanceId, config, activeConfig, modelVersions]
  );

  const handleSetModelVersion = useCallback(
    (
      key: keyof DataBuilderModelVersions,
      value: number | string | undefined
    ) => {
      setModelVersion(key, value);

      const baseUpdatedVersions = { ...modelVersions, [key]: value };
      const dependentIdKey = getDependentTaxonomyIdKey(key);
      const versionsAfterTaxonomyUpdate = (() => {
        if (dependentIdKey === undefined) {
          return baseUpdatedVersions;
        }

        const idModel = taxonomyIdModels.find((m) => m.key === dependentIdKey);
        if (idModel === undefined) {
          return baseUpdatedVersions;
        }

        const latestId = getLatestVisibleTaxonomyIdValue(
          idModel,
          baseUpdatedVersions,
          activeConfig?.settings
        );
        setModelVersion(
          dependentIdKey as keyof DataBuilderModelVersions,
          latestId
        );

        return {
          ...baseUpdatedVersions,
          [dependentIdKey]: latestId,
        };
      })();

      const dependentPatchKey = getDependentPatchVersionKey(key);
      if (dependentPatchKey === undefined) {
        return;
      }

      const patchModel = modelVersionModels.find(
        (model) => model.key === dependentPatchKey
      );
      if (patchModel === undefined) {
        return;
      }

      const latestPatchVersion = getLatestVisiblePatchVersionValue(
        patchModel,
        versionsAfterTaxonomyUpdate,
        activeConfig?.settings
      );
      setModelVersion(
        dependentPatchKey as keyof DataBuilderModelVersions,
        latestPatchVersion
      );
    },
    [
      setModelVersion,
      taxonomyIdModels,
      modelVersionModels,
      modelVersions,
      activeConfig?.settings,
    ]
  );

  const getAvailableUoaValues = useCallback(
    (
      datasetKey: string,
      settings: DatasetInstanceConfig['settings']
    ): string[] => {
      const deliverableConfig = getDeliverableColumnConfig(
        data?.deliverablesConfig ?? {},
        datasetKey
      );
      if (deliverableConfig === undefined) {
        return [];
      }
      const conditionContext = buildConditionContext(
        modelVersions,
        null,
        settings
      );
      return filterUnitOptionsByModels(
        deliverableConfig.unitOfAnalysis.options,
        conditionContext
      ).map((option) => option.value);
    },
    [data?.deliverablesConfig, modelVersions]
  );

  const canRenderContent =
    activeInstanceId !== null &&
    activeConfig !== undefined &&
    config !== undefined;

  return (
    <DataBuilderStepLayout
      title="Columns & Filters"
      footer={
        <DataBuilderFooter
          actions={
            <Tooltip
              label={rowLimitContinueDisabledReason}
              isDisabled={!isRowLimitContinueDisabled}
              hasArrow
              placement="top"
              shouldWrapChildren
            >
              <Button
                onClick={handleContinueStep2}
                disabled={isRowLimitContinueDisabled}
                color="primary"
                size="sm"
                data-testid="data-builder-step2-continue"
              >
                <HStack gap={2}>
                  {(isValidatingRowLimit || isWaitingForCompanies) && (
                    <Spinner size="xs" label="" />
                  )}
                  <span>Continue</span>
                </HStack>
              </Button>
            </Tooltip>
          }
        />
      }
      titleActions={
        availableModels.length > 0 ? (
          <Flex gap={2}>
            {isRevelioAdmin && <DataBuilderDataLagControl />}
            <TaxonomyPopover
              models={taxonomyIdModels}
              modelVersions={modelVersions}
              defaults={taxonomyDefaults}
              settings={activeConfig?.settings}
              onSetModelVersion={handleSetModelVersion}
              onResetModelVersions={resetModelVersions}
            />
            <ModelVersionsPopover
              models={modelVersionModels}
              modelVersions={modelVersions}
              defaults={modelVersionDefaults}
              settings={activeConfig?.settings}
              onSetModelVersion={handleSetModelVersion}
              onResetModelVersions={resetModelVersions}
            />
          </Flex>
        ) : null
      }
      noGutter
    >
      <LoadingBox
        loading={isLoading}
        height="100%"
        minHeight={0}
        overflow="hidden"
        marginBottom="0"
        marginTop="0"
      >
        <Flex height="100%" minHeight={0} overflow="hidden">
          <Step2DatasetSwitcher
            instanceIdsWithErrors={instanceIdsWithErrors}
            showValidationErrors={showValidationErrors}
            rowLimitResultsByInstanceId={rowLimitResultsByInstanceId}
            pendingInstanceIds={pendingInstanceIds}
            apiErrorInstanceIds={apiErrorInstanceIds}
            isWaitingForCompanies={isWaitingForCompanies}
          />
          <Box
            flex={1}
            minHeight={0}
            overflow="auto"
            minWidth={0}
            px={5}
            mb="4"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}
            >
              {isRowLimitError && (
                <Alert
                  status="error"
                  data-testid="data-builder-step2-row-limit-error"
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Row limit validation failed</AlertTitle>
                    <AlertDescription>{rowLimitErrorMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}
              {canRenderContent && (
                <Step2ColumnsContent
                  config={config}
                  activeConfig={activeConfig}
                  activeInstanceId={activeInstanceId}
                  modelVersions={modelVersions}
                  showValidationErrors={showValidationErrors}
                  sectionErrors={sectionErrors}
                  onSetSetting={handleSetSetting}
                  onSetUnitOfAnalysis={(value) => {
                    setUnitOfAnalysis(activeInstanceId, value);

                    if (value === null) {
                      return;
                    }

                    const freshState = dataBuilderStore.getState();
                    const freshConfig = freshState.configByInstance;
                    const orderedInstanceIds = freshState.datasetInstances.map(
                      (instance) => instance.instanceId
                    );

                    for (const instance of freshState.datasetInstances) {
                      if (instance.instanceId === activeInstanceId) {
                        continue;
                      }
                      const instanceConfig = freshConfig[instance.instanceId];
                      if (
                        instanceConfig === undefined ||
                        instanceConfig.unitOfAnalysis !== null
                      ) {
                        continue;
                      }
                      const availableValues = getAvailableUoaValues(
                        instance.datasetKey,
                        instanceConfig.settings
                      );
                      const pickedUoa = pickLastUsedUoaForTarget({
                        configByInstance: freshConfig,
                        orderedInstanceIds,
                        availableUoaValues: availableValues,
                        excludeInstanceId: instance.instanceId,
                      });
                      if (pickedUoa === null) {
                        continue;
                      }

                      applyInheritedUoa(
                        {
                          setUnitOfAnalysis,
                          setUnitOfAnalysisSelections,
                          setCompanySelections,
                          setCompanySets,
                          setCompanyMapper,
                        },
                        instance.instanceId,
                        pickedUoa,
                        freshConfig[pickedUoa.sourceInstanceId]
                      );
                    }
                  }}
                  onSetColumnKeysForBreakdown={setColumnKeysForBreakdown}
                  onSetColumnKey={(breakdownId, key, selected) =>
                    setColumnKey(activeInstanceId, breakdownId, key, selected)
                  }
                  onApplyCompanySelections={(selections) =>
                    setCompanySelections(activeInstanceId, selections)
                  }
                  onApplyCompanyMapper={(mapper) =>
                    applyCompanyMapper(activeInstanceId, mapper)
                  }
                  onApplyUnitOfAnalysisSelections={(items) => {
                    setUnitOfAnalysisSelections(activeInstanceId, items);

                    // Read fresh state from store since type propagation
                    // from onSetUnitOfAnalysis may not be reflected in
                    // the React closure's configByInstance yet.
                    const freshConfig =
                      dataBuilderStore.getState().configByInstance;
                    const activeUoa =
                      freshConfig[activeInstanceId]?.unitOfAnalysis ?? null;
                    if (activeUoa === null) {
                      return;
                    }

                    for (const instance of datasetInstances) {
                      if (instance.instanceId === activeInstanceId) {
                        continue;
                      }
                      const instanceConfig = freshConfig[instance.instanceId];
                      if (
                        instanceConfig === undefined ||
                        instanceConfig.unitOfAnalysis !== activeUoa ||
                        instanceConfig.unitOfAnalysisSelections.length > 0
                      ) {
                        continue;
                      }
                      setUnitOfAnalysisSelections(instance.instanceId, items);
                    }
                  }}
                />
              )}
              {!canRenderContent && (
                <Text color="text.secondary" fontSize="sm">
                  Select a dataset to configure columns and filters.
                </Text>
              )}
            </div>
          </Box>
        </Flex>
      </LoadingBox>
    </DataBuilderStepLayout>
  );
};
